import type { SagaReturnType } from 'redux-saga/effects';
import { call, takeEvery, select, put, takeLatest } from 'redux-saga/effects';

import type { postPackagesPrebook } from '@lt/api/dist/methods/packages/types';

import { touristsSelector } from 'src/store/tourists/selectors';

import { api } from 'src/api';
import { getClientData } from 'src/store/client/selectors';

import {
  incrementTryCount,
  setSuccess,
  setBookingId,
  onTryPrebook,
  onPrebookFailed,
  onPrebookSuccess,
  setInProgress,
  onPrebookSkip,
  setPending,
} from './slice';

import { getIsPrebookPending } from './selectors';
import { getOrderFlight } from '../../selectors/order';
import { getPackageId } from '../../selectors/package';
import {
  getPrebookData,
  getActualizationFetching,
} from '../../selectors/actualization';

import { actualizePackageSuccess } from '../../actions/actualization';
import { validationSuccessAction } from '../../validation/actions';
import { DOC_TYPES } from '../../constants/tourists';

function* actualizatonSuccessHandler() {
  const isPending: SagaReturnType<typeof getIsPrebookPending> = yield select(
    getIsPrebookPending,
  );
  if (isPending) {
    yield put(setPending(false));
    yield call(tryPrebookHandler);
  }
}

function* tryPrebook() {
  const isActualizationFetching: SagaReturnType<
    typeof getActualizationFetching
  > = yield select(getActualizationFetching);
  if (isActualizationFetching) {
    yield put(setPending(true));
  } else {
    yield call(tryPrebookHandler);
  }
}

function* tryPrebookHandler() {
  const tourists: SagaReturnType<typeof touristsSelector> = yield select(
    touristsSelector,
  );
  const prebookData: SagaReturnType<typeof getPrebookData> = yield select(
    getPrebookData,
  );
  const flightPairId: SagaReturnType<typeof getOrderFlight> = yield select(
    getOrderFlight,
  );
  const packageId: SagaReturnType<typeof getPackageId> = yield select(
    getPackageId,
  );
  const clientData: SagaReturnType<typeof getClientData> = yield select(
    getClientData,
  );

  if (
    !(
      clientData &&
      clientData.email &&
      clientData.phone &&
      clientData.name &&
      clientData.surname
    )
  ) {
    throw new Error(
      `tryPrebookHandler не корректный clientData: ${clientData}`,
    );
  }

  const client = {
    email: clientData.email,
    phone: clientData.phone,
    full_name: `${clientData.name} ${clientData.surname}`,
  };

  const requestParams: postPackagesPrebook = {
    flight_pair_id: flightPairId || '',
    package_id: packageId,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    prebook_data: prebookData as any,
    client,
    tourists: tourists.map((t) => ({
      name: t.name || '',
      surname: t.surname || '',
      birthday: t.birthday || '',
      passport_number: t.passport_number || '',
      // Валидатор на бэке требует строку, при этом с бэка может вернуться турист с null в этом поле (нужно попросить бэкендеров поправить)
      ...(t.docType === DOC_TYPES.international_passport.id
        ? { passport_expires_on: t.passport_expires_on || '' }
        : {}),
      nationality_id: t.nationality_id,
      gender: t.gender,
      patronymic: t.patronymic,
      doc_type: t.docType,
    })),
  };

  try {
    yield put(setInProgress(true));
    yield put(incrementTryCount());

    const response = yield api.postPackagesPrebook(requestParams);
    const { booking_id, success } = response;

    if (success && Boolean(booking_id)) {
      yield put(setBookingId(booking_id));
      yield put(onPrebookSuccess());
    } else {
      yield put(onPrebookSkip());
    }
  } catch (e) {
    // убираем вторую попытку пребука
    yield put(onPrebookFailed());
  }
}

function* prebookFailedHandler() {
  yield put(setInProgress(false));
  yield put(setSuccess(false));
}

function* prebookSuccesHandler() {
  yield put(setInProgress(false));
  yield put(setSuccess(true));
}

function* prebookSkipHandler() {
  yield put(setInProgress(false));
  yield put(setSuccess(false));
}

function* saga() {
  yield takeEvery([validationSuccessAction, onTryPrebook], tryPrebook);
  yield takeLatest(onPrebookFailed, prebookFailedHandler);
  yield takeEvery(onPrebookSuccess, prebookSuccesHandler);
  yield takeEvery(onPrebookSkip, prebookSkipHandler);
  yield takeLatest(actualizePackageSuccess, actualizatonSuccessHandler);
}

export default saga;
